import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowForward, EmojiEvents } from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import { baseURL } from "../../Constants";
import { useBadges } from "../../services/BadgeService";
import { GameBoard, getGameLeaderboard } from "../../services/MinigameService";
import GameBoardRows from "./GameBoardRows";
import { boardConfig } from "./gameBoards";
import styles from "./GameLeaderboard.module.css";

interface GameLeaderboardPanelProps {
    game: string;
    variant: string;
    /** Change it to refetch — the game bumps it whenever a run's score can have moved. */
    refreshKey?: number;
}

const PANEL_ROWS = 5;

/** This month's top 5 for one board, under the game itself, with the viewer's own rank. */
const GameLeaderboardPanel: React.FC<GameLeaderboardPanelProps> = ({ game, variant, refreshKey = 0 }) => {
    const { t } = useTranslation("pages");
    const { user } = useContext(AuthContext);
    const badges = useBadges();
    const config = boardConfig(game);
    const [board, setBoard] = useState<GameBoard | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getGameLeaderboard(game, variant, "month", PANEL_ROWS).then((b) => {
            if (cancelled) return;
            setBoard(b);
            setFailed(!b);
        });
        return () => { cancelled = true; };
    }, [game, variant, refreshKey, user?.steamID]);

    if (!config) return null;

    return (
        <section className={styles.panel}>
            <div className={styles.panelHead}>
                <span className={styles.panelTitle}>
                    <EmojiEvents sx={{ fontSize: 17, color: config.accent }} />
                    {t("gamesLeaderboard.panelTitle")}
                    <span className={styles.panelSub}>· {t("gamesLeaderboard.thisMonth")}</span>
                </span>
                <Link
                    to={`/games/leaderboard?game=${game}&variant=${variant}`}
                    className={styles.panelLink}
                >
                    {t("gamesLeaderboard.full")}
                    <ArrowForward sx={{ fontSize: 16 }} />
                </Link>
            </div>

            {failed && <div className={styles.note}>{t("gamesLeaderboard.error")}</div>}
            {!failed && !board && <div className={styles.note}>{t("gamesLeaderboard.loading")}</div>}
            {board && board.entries.length === 0 && <div className={styles.note}>{t("gamesLeaderboard.empty")}</div>}
            {board && board.entries.length > 0 && (
                <GameBoardRows config={config} entries={board.entries} me={board.me} badges={badges} />
            )}

            {!user && (
                <div className={styles.loginNudge}>
                    <span>{t("gamesLeaderboard.loginNudge")}</span>
                    <button className={styles.loginBtn} onClick={() => { window.location.href = baseURL + "/Auth/steam"; }}>
                        {t("gamesLeaderboard.login")}
                    </button>
                </div>
            )}
        </section>
    );
};

export default GameLeaderboardPanel;
